import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import { ApiError, apiFetch, apiFetchMultipart } from "@/lib/api";
import {
  apiErrorBodyForCapture,
  buildMarkupFormData,
  buildMultiPhotoFormData,
  isRetryable,
  outboxBackoff,
  outboxFingerprint,
} from "@/lib/outbox";
import {
  planKeys,
  projectKeys,
  recordKeys,
  type RecordFilters,
} from "@/lib/query-keys";
import { filterRecords } from "@/lib/records/filter-records";
import {
  deletePendingRecord,
  getAllPendingRecords,
  getPendingRecord,
  setPendingRecordServerId,
  updatePendingPhotoStatus,
  updatePendingRecordStatus,
  type PendingRecord,
} from "@/offline/db";
import { reconcilePendingRecords } from "@/offline/reconcile";
import { seedProvisionalThumbnail } from "@/offline/asset-backfill";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import { markRecordJustSynced } from "@/hooks/use-just-synced";
import type { PaginatedResponse, ProjectRecord } from "@/types/api";

/**
 * Retry-count threshold past which an outbox record is treated as "stuck".
 *
 * The outbox retries transient failures FOREVER and `isRetryable` classifies
 * an unknown error shape as transient — so a misclassified error retries
 * invisibly forever. By ~10 retries `outboxBackoff` has been pinned at its
 * ~10-minute cap for several cycles; a record still failing here is almost
 * certainly stuck on a misclassified error and ops should be told. See
 * [[error-reporting]] "Offline and outbox errors".
 */
const STUCK_RETRY_THRESHOLD = 10;

/** Variables for one upload attempt — only the stable local id is needed. */
interface UploadVars {
  clientUuid: string;
}

/**
 * Result of a settled upload mutation.
 *
 * `serverRecord` is the record the create produced — `null` only when the
 * outbox entry had already vanished before this record's FIRST upload
 * attempt could even start (a create→delete that fully completed before the
 * drain picked it up), in which case nothing was ever sent and nothing needs
 * reconciling.
 *
 * `deletedAtStart` snapshots, at the moment the upload began, whether the
 * entry was already flagged `deleted`. It is carried through so that even a
 * lost-response RETRY — by which time the `deleted` entry may have been
 * purged — still knows the delete intent and can fire the compensating
 * server `DELETE`. The "entry missing ⇒ nothing was sent" shortcut is only
 * safe before the very first attempt; after that the idempotent POST still
 * lands, so the intent must travel in the result, not be re-derived from a
 * possibly-gone entry.
 *
 * `projectId` / `uploadedRevision` are snapshotted from the outbox entry at
 * the moment the upload began so `onSuccess` can diff intent: a revision
 * advance means a follow-up `PATCH` (and possibly a markup `PATCH`) is owed,
 * a `deleted` snapshot or now-missing entry means a compensating `DELETE`.
 */
interface UploadResult {
  serverRecord: ProjectRecord | null;
  projectId: string | null;
  uploadedRevision: number;
  /** True if the entry was flagged `deleted` when the upload started. */
  deletedAtStart: boolean;
}

/**
 * JSON body for the follow-up metadata `PATCH` of a record edited
 * mid-upload.
 *
 * `metadata` is ALWAYS included — sending `{}` when the user cleared the
 * last metadata field is what actually clears it server-side; omitting it
 * would silently keep the stale value (see [[offline-record-outbox]] H1).
 */
function buildPatchFields(entry: PendingRecord): Record<string, unknown> {
  return {
    tags: entry.tags,
    plan: entry.planId,
    pin_x: entry.pinX,
    pin_y: entry.pinY,
    pin_bearing: entry.pinBearing,
    metadata: entry.metadata,
  };
}

/** Number of `ProjectImage`s on a server record — 0 when it carries none. */
function imageCount(record: ProjectRecord): number {
  return Array.isArray(record.images) ? record.images.length : 0;
}

/**
 * Drains ONE create entry by uploading each of its photos to the v2.0 records
 * endpoint under the entry's shared `clientUuid`.
 *
 * Every photo whose `uploadStatus !== "synced"` is fired CONCURRENTLY at the
 * v2.0 record endpoint under the entry's shared `clientUuid`
 * (= `client_record_uuid`), each carrying its own `X-Idempotency-Key`
 * (`photo.photoId` = `client_photo_uuid`). The backend get-or-creates one
 * logical record by `client_record_uuid` (DB-unique, any arrival order) and
 * appends the rest, so the responses CONVERGE: the one observed last holds the
 * most `images[]`. Photos already `synced` from a prior attempt are skipped —
 * persisted per-photo status is what makes a boot or retry re-fire only the
 * laggards, and idempotency keeps any redundant re-POST safe.
 *
 * Each fulfilled photo is marked `synced` immediately (so a later failure in
 * the same batch never re-uploads it). After settle:
 *  - ANY retryable rejection → leave those photos pending and throw the first
 *    transient error so the WHOLE mutation retries (synced photos skip next
 *    attempt). Correctness over a partial mark: a transient batch must re-run
 *    intact, never half-fail into a `failed` tile.
 *  - else (every rejection a permanent 4xx) → mark each failed photo `failed`
 *    with the server reason and throw the first permanent error, so `onError`
 *    flags the ENTRY `failed` for a tap-to-retry tile.
 *  - all photos synced → return the converged record (the one with the most
 *    `images`), shaped exactly like the single-photo `UploadResult` so the
 *    shared `onSuccess` handoff/reconcile is reused unchanged.
 */
async function uploadMultiPhotoRecord(
  entry: PendingRecord,
  projectId: string,
  uploadedRevision: number,
  deletedAtStart: boolean,
  getAccessToken: () => Promise<string>,
): Promise<UploadResult> {
  const { clientUuid } = entry;
  // The cover (photos[0]) is always present on a multi entry. Photos already
  // synced in a prior attempt are skipped — their bytes are already on the
  // server and the per-photo idempotency key would dedup them anyway.
  const photos = entry.photos ?? [];

  // The user deleted this entry. If its record was created server-side we know
  // the id (`serverRecordId`, recorded when the first photo synced), so fire the
  // compensating DELETE directly BY ID — no recovery re-POST (re-POSTing could
  // re-create a ghost if a prior delete already landed). The DELETE runs INSIDE
  // mutationFn, so a transient failure gets the mutation's retry/backoff (no
  // external invalidation loop) and a 404 means the record is already gone →
  // success. Returning a null record makes `onSuccess` simply purge the local
  // entry. With no recorded id there is nothing to delete → purge.
  if (deletedAtStart) {
    if (entry.serverRecordId) {
      try {
        await apiFetch<void>(
          `/projects/1.2/projects/${projectId}/records/${entry.serverRecordId}/`,
          getAccessToken,
          { method: "DELETE" },
        );
      } catch (err) {
        // 404 = already deleted → idempotent success. Re-throw anything else so
        // the mutation retries (transient) or settles in onError (permanent).
        if (!(err instanceof ApiError && err.status === 404)) throw err;
      }
    }
    return { serverRecord: null, projectId, uploadedRevision, deletedAtStart };
  }

  const pending = photos.filter((photo) => photo.uploadStatus !== "synced");

  // Normal path: fire the not-yet-synced photos. RECOVERY path: if every photo
  // is already `synced` — a prior attempt persisted all per-photo statuses but
  // the process died (reload/crash) before `onSuccess` removed the entry and
  // handed the record off — re-POST the cover ONCE. It is idempotent via its
  // `X-Idempotency-Key` (the backend returns the existing record, never
  // double-appends) and exists solely to recover the converged server record:
  // without it `onSuccess` would see a null record, treat the entry as "nothing
  // was sent", delete it, and NEVER place the real server-side record into the
  // grid — so the tile would vanish after crash recovery and never refetch
  // offline (Codex P1). `photos` is non-empty on a multi entry, so `slice(0, 1)`
  // is always the cover. See [[offline-record-outbox]].
  const toUpload = pending.length > 0 ? pending : photos.slice(0, 1);

  const results = await Promise.allSettled(
    toUpload.map((photo) =>
      apiFetchMultipart<ProjectRecord>(
        `/projects/2.0/projects/${projectId}/records/`,
        buildMultiPhotoFormData(entry, photo),
        getAccessToken,
        { headers: { "X-Idempotency-Key": photo.photoId } },
      ),
    ),
  );

  // The converged record: the fulfilled response with the most `images` (the
  // appends land incrementally, so the last writer sees the fullest set).
  let converged: ProjectRecord | null = null;
  const rejections: unknown[] = [];
  await Promise.all(
    results.map(async (result, index) => {
      const photo = toUpload[index];
      if (result.status === "fulfilled") {
        // Persist this photo as synced BEFORE anything can throw, so a later
        // permanent failure in the same batch never re-uploads it next attempt.
        await updatePendingPhotoStatus(clientUuid, photo.photoId, "synced");
        // Record the server record id the first time we learn it (idempotent —
        // every response for this `client_record_uuid` carries the same id) so a
        // later delete can compensate BY ID without a recovery re-POST.
        if (result.value.id) {
          await setPendingRecordServerId(clientUuid, result.value.id);
        }
        if (!converged || imageCount(result.value) > imageCount(converged)) {
          converged = result.value;
        }
      } else {
        rejections.push(result.reason);
      }
    }),
  );

  if (rejections.length === 0) {
    // Every photo landed — hand the converged record to the shared onSuccess,
    // shaped like the single-photo result. `converged` is non-null here
    // (`toUpload` is non-empty and all fulfilled); assert it so a later refactor
    // can't silently feed a null record into the success handoff.
    if (!converged) {
      throw new Error(
        "multi-photo upload settled with no rejections but no server record",
      );
    }
    return {
      serverRecord: converged,
      projectId,
      uploadedRevision,
      deletedAtStart,
    };
  }

  // Any transient rejection makes the WHOLE attempt transient: leave the failed
  // photos pending (a `synced` one stays synced and skips next attempt) and
  // throw the first transient error so the mutation retries forever.
  const firstTransient = rejections.find((reason) => isRetryable(reason));
  if (firstTransient !== undefined) {
    throw firstTransient;
  }

  // Every rejection is a permanent 4xx — mark each failed photo `failed` with
  // the server's reason, then throw the first so `onError` flags the entry.
  await Promise.all(
    results.map(async (result, index) => {
      if (result.status === "rejected") {
        const reason = result.reason;
        const message =
          reason instanceof ApiError
            ? (apiErrorBodyForCapture(reason) ?? `API error ${reason.status}`)
            : reason instanceof Error
              ? reason.message
              : String(reason);
        await updatePendingPhotoStatus(
          clientUuid,
          toUpload[index].photoId,
          "failed",
          message,
        );
      }
    }),
  );
  throw rejections[0];
}


/**
 * Refresh the record-list queries after an outbox change.
 *
 * The records list uses `refetchType: "all"` so a record that synced while the
 * photo grid was NOT mounted still refetches — `invalidateQueries` defaults to
 * `"active"`, which only refetches a query with a live observer, leaving the
 * synced record absent from the persisted offline cache. The outbox drain runs
 * online, so the forced refetch lands and the synced record becomes durable
 * offline. Project detail and plans keep the default — they are not the
 * offline-visibility concern this guards.
 */
function invalidateRecordLists(
  queryClient: QueryClient,
  projectId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: recordKeys.byProject(projectId),
    refetchType: "all",
  });
  void queryClient.invalidateQueries({
    queryKey: projectKeys.detail(projectId),
  });
  void queryClient.invalidateQueries({
    queryKey: planKeys.byProject(projectId),
  });
}

/**
 * Splice a just-synced record into the project's cached record-list queries
 * so the synced tile is present the instant the outbox tile is removed — the
 * cache half of the flicker-free handoff (see [[offline-record-outbox]]
 * § Success).
 *
 * Without this the synced record only appears once `invalidateRecordLists`
 * has completed a network round-trip, leaving a ~200-600ms gap where the
 * record is in neither the outbox nor the list — the visible flicker.
 *
 * Inserts into every ACTIVE record-list query for the project whose filters
 * the record satisfies, evaluated client-side with the same `filterRecords`
 * the offline grid uses. A query with an active `search` filter is skipped:
 * search is server-side semantic matching with no client-side equivalent, so
 * that view alone falls back to the `invalidateRecordLists` refetch.
 */
function insertSyncedRecordIntoListCaches(
  queryClient: QueryClient,
  projectId: string,
  record: ProjectRecord,
): void {
  const listQueries = queryClient.getQueryCache().findAll({
    queryKey: recordKeys.byProject(projectId),
    type: "active",
  });
  for (const query of listQueries) {
    const key = query.queryKey;
    // recordKeys.list(...) is ["records", projectId, "list", filters].
    if (key[2] !== "list") continue;
    const filters = (key[3] ?? {}) as RecordFilters;
    // Semantic search has no client-side equivalent — we cannot know whether
    // the new record matches, so leave that view to the refetch.
    if (filters.search) continue;
    // Only insert where the record actually satisfies this view's filters.
    if (filterRecords([record], filters).length === 0) continue;
    queryClient.setQueryData<InfiniteData<PaginatedResponse<ProjectRecord>>>(
      key,
      (old) => {
        if (!old || old.pages.length === 0) return old;
        // Already present (a concurrent refetch or reconcile beat us here).
        const present = old.pages.some((page) =>
          page.results.some((r) => r.id === record.id),
        );
        if (present) return old;
        const [firstPage, ...rest] = old.pages;
        return {
          ...old,
          pages: [
            {
              ...firstPage,
              count: firstPage.count + 1,
              results: [record, ...firstPage.results],
            },
            ...rest,
          ],
        };
      },
    );
  }
}

/**
 * Background sync engine for the offline record outbox.
 *
 * A single always-mounted hook that drains the durable `pendingRecords`
 * IndexedDB store: it uploads every queued record to the server, retries
 * transient failures forever with capped exponential backoff, pauses while
 * offline (auto-resuming on reconnect/refocus), and reconciles edits or
 * deletes that landed on an entry while its upload was in flight.
 *
 * See [[offline-record-outbox]] for the behavioral contract.
 *
 * Lifecycle of one record:
 *   1. The drain effect sees a `pending` entry not already in-flight and
 *      fires the uploader mutation, recording its `clientUuid` in a ref Set.
 *      A `deleted` entry never fires a fresh upload: if it is not in flight
 *      it was never created server-side and the effect just purges it; if it
 *      is in flight, step 4 reconciles it.
 *   2. `mutationFn` reads the entry, snapshots its `revision`/`projectId`
 *      and `deleted` flag, and POSTs each photo to the v2.0 records endpoint
 *      with `X-Idempotency-Key: <photoId>` (per photo) under a shared
 *      `client_record_uuid: <clientUuid>` so retries and reconcile races can
 *      never duplicate the record.
 *   3. `retry`/`retryDelay` keep transient failures (offline, 5xx, 429/503,
 *      network errors) retrying forever; `networkMode: "online"` pauses the
 *      whole mutation while offline instead of burning a permanent failure.
 *   4. `onSuccess` reconciles local intent — a delete (snapshotted at upload
 *      start or landed mid-upload) becomes a compensating server `DELETE`,
 *      an edit becomes a follow-up markup + metadata `PATCH` — then deletes
 *      the outbox entry and refreshes the grid.
 *   5. `onError` (only reached after `retry` permanently gives up on a 4xx)
 *      marks the entry `failed` so the grid shows a tap-to-retry tile.
 */
export function useOutboxDrain(): void {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  // `clientUuid`s with an upload mutation currently in flight. A ref (not
  // state) so adding/removing an entry never triggers a re-render — the
  // drain effect re-reads it synchronously when the pending list changes.
  const inFlightRef = useRef<Set<string>>(new Set());

  // `clientUuid`s that have fired at least one upload attempt this session.
  // Used by `mutationFn` to tell a genuine pre-first-attempt cancellation
  // (entry gone, never attempted ⇒ nothing sent) apart from a lost-response
  // retry (entry gone, but a prior attempt may have landed ⇒ must reconcile).
  const attemptedRef = useRef<Set<string>>(new Set());

  // Per-record bookkeeping for observability ONLY — never steers outbox
  // behaviour. `attemptCountRef` counts how many times `mutationFn` has run
  // for each uuid (1-indexed: the first upload is attempt 1). `onError`
  // reads it so a permanent-failure capture reports how many attempts the
  // record burned. `lastRetryErrorRef` keeps the most recent upload error
  // per record so the next attempt's transient-retry breadcrumb and the
  // stuck alert can name the error class that is looping. `lastProjectIdRef`
  // caches each uuid's projectId so a capture can report it without an
  // extra IDB read. All three are cleared in `onSettled`.
  const attemptCountRef = useRef<Map<string, number>>(new Map());
  const lastRetryErrorRef = useRef<Map<string, unknown>>(new Map());
  const lastProjectIdRef = useRef<Map<string, string>>(new Map());

  // `clientUuid`s already reported to Sentry as "stuck" this session. An
  // in-memory guard (v1 design — no persisted schema field): the stuck
  // message fires exactly ONCE per record per session. A reload that finds
  // the record still stuck may re-alert; that is acceptable.
  const stuckReportedRef = useRef<Set<string>>(new Set());

  // `clientUuid`s whose `onSuccess` already captured a reconcile failure (a
  // compensating-delete or follow-up-update throw). TanStack routes an
  // `onSuccess` that throws into `onError`, so without this guard `onError`
  // would capture the SAME failure a second time, mis-tagged as a permanent
  // upload failure. `onError` consults this Set and skips its own capture
  // when the failure was already reported with the accurate phase tag.
  const reconcileFailureReportedRef = useRef<Set<string>>(new Set());

  // `clientUuid`s whose compensating DELETE (for a deleted multi entry that was
  // created server-side) has been fired this session. The drain-loop `deleted`
  // branch fires that compensation through the uploader ONCE per entry: a
  // transient DELETE failure is retried INSIDE the mutation (backoff), and this
  // guard stops a PERMANENT failure (e.g. a 403 after losing edit permission)
  // from re-firing on every `["pendingRecords"]` invalidation — which would be a
  // tight, un-throttled retry loop. A fresh session retries (the deleted entry
  // persists), so it is eventually compensated once the permanent cause clears.
  const compensatingRef = useRef<Set<string>>(new Set());

  // Reconcile-on-boot gate. `reconcileStartedRef` ensures the reconcile pass
  // runs exactly ONCE per app session, not on every drain tick. `reconcile-
  // DoneRef` is flipped to `true` once reconcile has settled (success OR
  // failure) — until then the drain effect holds off so it can't re-upload a
  // record reconcile is about to confirm as already-landed. The hold is
  // bounded: reconcile is a handful of fast GETs and degrades gracefully on
  // failure, so the drain always proceeds shortly after boot.
  const reconcileStartedRef = useRef(false);
  const reconcileDoneRef = useRef(false);
  // Forces a re-render when reconcile settles so the gated drain effect
  // re-runs and starts uploading the `unknown` survivors.
  const [, setReconcileTick] = useState(0);

  // Observe the WHOLE outbox (every project). Invalidating the
  // `["pendingRecords"]` prefix — which submit and tap-to-retry now do —
  // refetches this query, so a freshly queued or re-queued record is
  // replayed automatically; on a fresh boot the initial fetch replays any
  // records left pending from a prior session.
  const { data: pendingRecords } = useQuery({
    queryKey: ["pendingRecords"],
    queryFn: getAllPendingRecords,
    // The outbox is local IndexedDB; only submit/drain/retry mutate it and
    // they invalidate this key explicitly. No background refetch churn.
    staleTime: Infinity,
    gcTime: 0,
  });

  // Outbox health context — observability only. Whenever the pending list
  // changes, stamp the queue depth onto Sentry's GLOBAL scope so EVERY event
  // captured anywhere in the app (not just outbox errors) carries the outbox
  // state at the time it fired. See [[error-reporting]].
  useEffect(() => {
    if (!pendingRecords) return;
    let pending = 0;
    let failed = 0;
    for (const entry of pendingRecords) {
      if (entry.status === "failed") failed += 1;
      else if (entry.status === "pending" && !entry.deleted) pending += 1;
    }
    Sentry.getGlobalScope().setContext("outbox", { pending, failed });
  }, [pendingRecords]);

  const uploader = useMutation<UploadResult, Error, UploadVars>({
    mutationKey: ["outbox", "upload"],
    // Pause (don't fail) the whole mutation while offline; TanStack's
    // QueryClient auto-resumes paused mutations on reconnect/refocus.
    networkMode: "online",
    // Retry-forever for transient failures; a permanent 4xx stops here.
    // The retry verdict is `isRetryable` alone — observability never steers
    // it. (`retry` cannot identify which `clientUuid` it is firing for —
    // multiple uploads run concurrently — so the per-record breadcrumb,
    // attempt count and stuck alert all live in `mutationFn`, which DOES
    // receive `{ clientUuid }` and runs once per attempt.)
    retry: (_failureCount, error) => isRetryable(error),
    retryDelay: outboxBackoff,
    mutationFn: async ({ clientUuid }): Promise<UploadResult> => {
      // Per-attempt counter (observability only — never steers behaviour).
      // `mutationFn` runs once per attempt and reliably knows `clientUuid`,
      // so it is the correct per-record hook. First call = attempt 1.
      const attempt = (attemptCountRef.current.get(clientUuid) ?? 0) + 1;
      attemptCountRef.current.set(clientUuid, attempt);

      if (attempt === 1) {
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "info",
          message: "outbox drain attempt started",
          data: { clientUuid, attempt },
        });
      } else {
        // Reaching attempt N (N>1) means attempt N-1 failed transiently —
        // `retry` would not have re-fired `mutationFn` otherwise. Leave a
        // transient-retry breadcrumb naming the error that is being retried.
        const prevError = lastRetryErrorRef.current.get(clientUuid);
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "info",
          message: "outbox upload transient retry",
          data: {
            clientUuid,
            attempt,
            status:
              prevError instanceof ApiError ? prevError.status : undefined,
          },
        });
        // Stuck-record detection: a transient error retrying past the
        // threshold can never drain. Fire ONCE per record per session — the
        // in-memory `stuckReportedRef` Set is the only guard (v1: no
        // persisted schema field; a reload may re-alert, which is fine).
        if (
          attempt >= STUCK_RETRY_THRESHOLD &&
          !stuckReportedRef.current.has(clientUuid)
        ) {
          stuckReportedRef.current.add(clientUuid);
          Sentry.captureMessage("Outbox record stuck retrying", {
            level: "warning",
            tags: { area: "outbox", phase: "stuck-retrying" },
            fingerprint: outboxFingerprint("stuck-retrying", prevError),
            contexts: {
              outbox: {
                clientUuid,
                projectId: lastProjectIdRef.current.get(clientUuid),
                attempts: attempt,
                lastError:
                  prevError === undefined || prevError === null
                    ? undefined
                    : (prevError as object).constructor.name,
                status:
                  prevError instanceof ApiError ? prevError.status : undefined,
                body: apiErrorBodyForCapture(prevError),
              },
            },
          });
        }
      }

      const entry = await getPendingRecord(clientUuid);
      if (!entry) {
        // The entry is gone. This is only safe to treat as "nothing sent"
        // BEFORE the first attempt — `attemptedRef` records every uuid that
        // has fired at least one POST. If this uuid was attempted before,
        // the create may already have landed (a lost-response retry), so we
        // must NOT short-circuit; the drain effect only fires `mutate` for
        // a never-attempted gone entry as a delete-purge, never to upload.
        return {
          serverRecord: null,
          projectId: null,
          uploadedRevision: 0,
          deletedAtStart: false,
        };
      }
      // Snapshot revision + projectId + the `deleted` flag BEFORE the
      // network call. The `deleted` snapshot rides in the result so even a
      // lost-response retry — after the `deleted` entry has been purged —
      // still knows to fire the compensating DELETE in `onSuccess`.
      const uploadedRevision = entry.revision;
      const deletedAtStart = entry.deleted;
      const { projectId } = entry;
      // Remember the projectId for this uuid so a later stuck-record alert
      // (whose attempt re-read the entry but may not need projectId again)
      // and `onError` can report it without another IDB read.
      lastProjectIdRef.current.set(clientUuid, projectId);
      // Mark this uuid as attempted so a future retry whose entry has since
      // vanished still POSTs (idempotently) rather than wrongly short-
      // circuiting as "nothing sent".
      attemptedRef.current.add(clientUuid);

      // All creates drain via v2.0: every photo of the record uploads to the
      // v2.0 endpoint under the shared `clientUuid` (= client_record_uuid),
      // each with its own per-photo idempotency key.
      try {
        return await uploadMultiPhotoRecord(
          entry,
          projectId,
          uploadedRevision,
          deletedAtStart,
          getAccessToken,
        );
      } catch (err) {
        // Remember the error so the NEXT attempt's transient-retry breadcrumb
        // (and a stuck alert) can name what is looping. Re-throw untouched —
        // `retry`/`onError` own the actual failure handling.
        lastRetryErrorRef.current.set(clientUuid, err);
        throw err;
      }
    },
    onSuccess: async (result, { clientUuid }) => {
      const { serverRecord, projectId, uploadedRevision, deletedAtStart } =
        result;

      // The entry vanished before this record's first attempt — nothing was
      // ever sent. Drop any trace and refresh the grid; no reconcile owed.
      if (!serverRecord || !projectId) {
        await deletePendingRecord(clientUuid);
        attemptedRef.current.delete(clientUuid);
        void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
        return;
      }

      Sentry.addBreadcrumb({
        category: "outbox",
        level: "info",
        message: "outbox upload succeeded",
        data: {
          clientUuid,
          projectId,
          serverRecordId: serverRecord.id,
          attempts: attemptCountRef.current.get(clientUuid),
        },
      });

      // Re-read the entry to reconcile any edit/delete that landed while the
      // upload was in flight. The create has already succeeded server-side.
      const current = await getPendingRecord(clientUuid);

      // The record's canonical server state, used after reconcile to seed the
      // offline asset cache. Starts as the create response, advances to the
      // markup-PATCH response on a mid-upload edit, and becomes `null` once a
      // compensating delete removes the record.
      //
      // Normalized to the canonical (v1.1 list) image shape here: the create /
      // edit endpoints are v1.2, so their `image` object omits the `image` /
      // `thumbnail` keys the grid reads. Splicing it in raw left those fields
      // undefined and crashed the Photos area. See `normalizeRecordImageShape`.
      let canonicalRecord: ProjectRecord | null =
        normalizeRecordImageShape(serverRecord);

      if (deletedAtStart || !current || current.deleted) {
        // The user deleted this pending record — either before the upload
        // started (`deletedAtStart`, carried through a lost-response retry)
        // or while it was in flight (`current.deleted`), or the `deleted`
        // entry was already purged (`!current`). The create still landed,
        // so fire a compensating server DELETE for the real record.
        //
        // The record will not exist server-side after this, so there is
        // nothing to seed into the offline asset cache.
        canonicalRecord = null;
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "info",
          message: "outbox compensating delete fired",
          data: { clientUuid, projectId, serverRecordId: serverRecord.id },
        });
        try {
          await apiFetch<void>(
            `/projects/1.2/projects/${projectId}/records/${serverRecord.id}/`,
            getAccessToken,
            { method: "DELETE" },
          );
        } catch (err) {
          // A failed compensating delete leaves a GHOST record on the server
          // — the create landed but the delete the user asked for did not.
          // Capture it, then re-throw UNCHANGED so the existing outcome
          // (entry NOT purged, mutation settles errored) is exactly as
          // before — this is observability only. Record the uuid so the
          // `onError` that TanStack routes this re-throw into does not
          // capture the same failure again under the wrong phase tag.
          reconcileFailureReportedRef.current.add(clientUuid);
          Sentry.captureException(err, {
            level: "error",
            tags: { area: "outbox", phase: "compensating-delete" },
            fingerprint: outboxFingerprint("compensating-delete", err),
            contexts: {
              outbox: {
                clientUuid,
                projectId,
                serverRecordId: serverRecord.id,
                status: err instanceof ApiError ? err.status : undefined,
                body: apiErrorBodyForCapture(err),
              },
            },
          });
          throw err;
        }
      } else if (current.revision !== uploadedRevision) {
        // The record was edited after the upload snapshot was taken — the
        // create sent the stale version. The `revision` bump covers BOTH a
        // metadata edit and a markup re-edit, and the drain can't cheaply
        // tell them apart, so reconcile ALL of it: the multipart markup
        // PATCH (image + original + annotations) AND the metadata PATCH.
        // No edit may be silently lost (see [[offline-record-outbox]] H2).
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "info",
          message: "outbox follow-up update fired",
          data: { clientUuid, projectId, serverRecordId: serverRecord.id },
        });
        try {
          // Two sequential PATCHes reconcile the mid-upload edit: a markup
          // PATCH (image + original + annotations), then a metadata PATCH
          // (tags, plan, pin, metadata). The metadata PATCH runs last, so its
          // response — reflecting BOTH edits — is the record's canonical
          // post-edit state. That response becomes `canonicalRecord`: it is
          // spliced into the grid's record-list cache and seeded into the
          // offline asset cache. The markup response carries stale
          // tags/plan/pin and must not be used for either.
          await apiFetchMultipart<ProjectRecord>(
            `/projects/1.2/projects/${projectId}/records/${serverRecord.id}/`,
            buildMarkupFormData(current),
            getAccessToken,
            { method: "PATCH" },
          );
          canonicalRecord = normalizeRecordImageShape(
            await apiFetch<ProjectRecord>(
              `/projects/1.2/projects/${projectId}/records/${serverRecord.id}/`,
              getAccessToken,
              {
                method: "PATCH",
                body: JSON.stringify(buildPatchFields(current)),
              },
            ),
          );
        } catch (err) {
          // A failed follow-up update means an edit the user made
          // mid-upload was silently dropped server-side. Capture it, then
          // re-throw UNCHANGED so behaviour is identical to before. Record
          // the uuid so the `onError` this re-throw is routed into does not
          // re-capture the same failure under the wrong phase tag.
          reconcileFailureReportedRef.current.add(clientUuid);
          Sentry.captureException(err, {
            level: "error",
            tags: { area: "outbox", phase: "follow-up-update" },
            fingerprint: outboxFingerprint("follow-up-update", err),
            contexts: {
              outbox: {
                clientUuid,
                projectId,
                serverRecordId: serverRecord.id,
                status: err instanceof ApiError ? err.status : undefined,
                body: apiErrorBodyForCapture(err),
              },
            },
          });
          throw err;
        }
      }

      await deletePendingRecord(clientUuid);
      attemptedRef.current.delete(clientUuid);

      // Seed the just-synced photo into the offline asset cache — the
      // in-memory render cache AND IndexedDB — from the local capture bytes,
      // BEFORE the handoff below. Warming the render cache first means the
      // synced tile's OfflineImage resolves the photo synchronously on its
      // first render, with no muted-placeholder flash; the IDB write keeps a
      // pinned project's photo durable offline even if connectivity drops
      // immediately. The backfill subscriber upgrades this provisional blob to
      // the canonical server thumbnail later. `current` is non-null whenever
      // `canonicalRecord` is — only the compensating-delete branch nulls it.
      // Best-effort: the helper never throws, so it cannot derail the sync.
      if (canonicalRecord && current) {
        await seedProvisionalThumbnail(
          canonicalRecord,
          projectId,
          current.annotatedBlob,
        );
      }

      if (canonicalRecord) {
        // Atomic handoff: place the synced server record into the record-list
        // cache(s) AND drop the outbox entry from the pending caches in the
        // SAME synchronous tick, so React batches them into one render — the
        // outbox tile and the synced tile swap with no frame where the record
        // belongs to neither. Without this the tile vanishes for the duration
        // of the `invalidateRecordLists` network refetch — the visible
        // flicker. See [[offline-record-outbox]] § Success.
        insertSyncedRecordIntoListCaches(
          queryClient,
          projectId,
          canonicalRecord,
        );
        // The prefix also matches an open viewer's per-record query
        // (`["pendingRecords", "photos", id]`), whose data is ONE entry, not a
        // list — only the list-shaped queries are filtered; the rest are left
        // for the `invalidateQueries` below.
        queryClient.setQueriesData<PendingRecord[]>(
          { queryKey: ["pendingRecords"] },
          (old) =>
            Array.isArray(old)
              ? old.filter((entry) => entry.clientUuid !== clientUuid)
              : old,
        );
        // Flag the record for the brief "Synced" confirmation badge.
        markRecordJustSynced(canonicalRecord.id);
      }

      void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
      invalidateRecordLists(queryClient, projectId);
    },
    onError: (error, { clientUuid }) => {
      // Reached when `retry` permanently gave up — a 4xx the server actively
      // rejected — OR when an `onSuccess` reconcile step threw (TanStack
      // routes a throwing `onSuccess` here). Either way mark the entry
      // `failed` so the grid renders a tap-to-retry tile. Never a popup. A
      // transient upload failure never lands here; it just keeps retrying.
      //
      // The permanent-upload-failure capture is the most important capture:
      // the server REFUSED a queued capture and nobody is watching a
      // background upload. Report it with outbox context so ops can triage
      // without a stack trace (see [[error-reporting]]). But skip it when
      // `onSuccess` already captured this same error as a compensating-
      // delete / follow-up-update failure — re-capturing it here would
      // double-count it under the wrong phase tag.
      if (reconcileFailureReportedRef.current.has(clientUuid)) {
        reconcileFailureReportedRef.current.delete(clientUuid);
      } else {
        Sentry.captureException(error, {
          level: "error",
          tags: { area: "outbox", phase: "upload-permanent-failure" },
          fingerprint: outboxFingerprint("upload-permanent-failure", error),
          contexts: {
            outbox: {
              clientUuid,
              projectId: lastProjectIdRef.current.get(clientUuid),
              status: error instanceof ApiError ? error.status : undefined,
              body: apiErrorBodyForCapture(error),
              attempts: attemptCountRef.current.get(clientUuid),
            },
          },
        });
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "error",
          message: "outbox upload permanently failed",
          data: {
            clientUuid,
            projectId: lastProjectIdRef.current.get(clientUuid),
            status: error instanceof ApiError ? error.status : undefined,
            attempts: attemptCountRef.current.get(clientUuid),
          },
        });
      }
      void updatePendingRecordStatus(clientUuid, "failed").then(() => {
        void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
      });
    },
    onSettled: (_data, _error, { clientUuid }) => {
      // Whatever the outcome, this upload is no longer in flight — clear the
      // guard so the drain effect may fire it again (e.g. tap-to-retry).
      inFlightRef.current.delete(clientUuid);
      // Drop this upload's per-record observability bookkeeping. A
      // subsequent tap-to-retry is a fresh attempt sequence — it should
      // count from attempt 1 again, not inherit the failed run's count.
      // `stuckReportedRef` is deliberately NOT cleared: a record reported
      // stuck stays reported for the rest of the session.
      attemptCountRef.current.delete(clientUuid);
      lastRetryErrorRef.current.delete(clientUuid);
      lastProjectIdRef.current.delete(clientUuid);
      // Defensive: clear the reconcile-failure flag too in case `onError`
      // did not run for this uuid (it always should — a throwing `onSuccess`
      // routes here — but never leak a stale entry).
      reconcileFailureReportedRef.current.delete(clientUuid);
    },
  });

  // Reconcile-on-boot. Once per app session, before the drain uploads
  // anything, ask the server which queued records already landed in a prior
  // session (e.g. an upload that succeeded but whose response was lost) and
  // drop those — skipping a wasteful re-upload of megabytes.
  //
  // For each `acked` uuid: delete the outbox entry and invalidate the
  // pending + record-list queries (the record is already on the server).
  // For each `unknown` uuid: leave it; the gated drain effect below uploads
  // it normally. Reconcile is a bandwidth optimisation only — idempotency,
  // not reconcile, guarantees correctness, so a slow or failing reconcile
  // still ends with `reconcileDoneRef` flipped and the drain proceeding.
  useEffect(() => {
    if (reconcileStartedRef.current) return;
    // Wait for the first outbox snapshot before deciding what to reconcile.
    if (!pendingRecords) return;
    reconcileStartedRef.current = true;

    // Nothing queued from a prior session — open the gate immediately.
    if (pendingRecords.length === 0) {
      reconcileDoneRef.current = true;
      setReconcileTick((n) => n + 1);
      return;
    }

    const clientUuids = pendingRecords.map((entry) => entry.clientUuid);

    Sentry.addBreadcrumb({
      category: "outbox",
      level: "info",
      message: "outbox reconcile-on-boot started",
      data: { queued: clientUuids.length },
    });

    void (async () => {
      try {
        const statuses = await reconcilePendingRecords(
          clientUuids,
          getAccessToken,
        );
        const ackedProjectIds = new Set<string>();
        let ackedCount = 0;
        for (const entry of pendingRecords) {
          if (statuses.get(entry.clientUuid) !== "acked") continue;
          // Drop the entry: `acked` means a prior v1.2 upload landed (the
          // backend's RequestLog is keyed on `clientUuid`). Genuine v2.0
          // entries key reconcile on per-photo `photoId`s — never the record
          // `clientUuid` — so they are NEVER acked. Dropping here is therefore
          // safe: the only entries that can be acked are landed-legacy ones,
          // and re-POSTing them via v2.0 would reuse the same `clientUuid` as
          // a different endpoint's idempotency key, yielding a spurious 409.
          await deletePendingRecord(entry.clientUuid);
          ackedProjectIds.add(entry.projectId);
          ackedCount += 1;
        }
        if (ackedProjectIds.size > 0) {
          void queryClient.invalidateQueries({
            queryKey: ["pendingRecords"],
          });
          for (const projectId of ackedProjectIds) {
            invalidateRecordLists(queryClient, projectId);
          }
        }
        Sentry.addBreadcrumb({
          category: "outbox",
          level: "info",
          message: "outbox reconcile-on-boot result",
          data: { queued: clientUuids.length, acked: ackedCount },
        });
      } catch (err) {
        // `reconcilePendingRecords` degrades internally and is not expected
        // to reject — but if it ever does, the drain still proceeds (every
        // still-pending entry just uploads normally below). It is NOT
        // log-and-swallow: an unexpected reject means reconcile is broken,
        // so report it (warning — idempotency keeps the drain correct).
        // See [[error-reporting]] "never log-and-swallow".
        Sentry.captureException(err, {
          level: "warning",
          tags: { area: "outbox", phase: "reconcile" },
          fingerprint: outboxFingerprint("reconcile", err),
          contexts: {
            outbox: {
              status: err instanceof ApiError ? err.status : undefined,
              body: apiErrorBodyForCapture(err),
            },
          },
        });
      } finally {
        // Open the drain gate whatever happened — reconcile must never block
        // the upload path forever.
        reconcileDoneRef.current = true;
        setReconcileTick((n) => n + 1);
      }
    })();
  }, [pendingRecords, getAccessToken, queryClient]);

  // The drain loop. For every outbox entry:
  //   - `deleted` + NOT in flight + already created server-side (a multi entry
  //     with any `synced` photo) → route through the uploader to recover the
  //     record id and fire a compensating server DELETE (never a fresh create).
  //   - `deleted` + NOT in flight + never created (single, or multi with no
  //     synced photo) → purge the entry locally — no upload, no server call.
  //   - `deleted` + in flight → leave it. The compensating server DELETE is
  //     `onSuccess`'s job once the in-flight create settles.
  //   - `pending` + NOT in flight → fire the upload.
  //   - `failed` / in flight → skip.
  // Adding the uuid to the in-flight Set BEFORE calling `mutate` guards
  // against the same record being fired twice when this effect re-runs
  // (e.g. the pending list refetches mid-upload).
  //
  // Gated on `reconcileDoneRef`: until the boot reconcile settles the drain
  // holds off, so an entry reconcile is about to confirm as already-landed
  // is never re-uploaded. The gate flips on the very next session and the
  // reconcile effect's `setReconcileTick` re-runs this effect to start.
  useEffect(() => {
    if (!reconcileDoneRef.current) return;
    if (!pendingRecords) return;
    let purgedAny = false;
    for (const entry of pendingRecords) {
      const inFlight = inFlightRef.current.has(entry.clientUuid);
      if (entry.deleted) {
        if (!inFlight) {
          // A deleted MULTI entry whose record was created server-side has its
          // `serverRecordId` recorded (set when the first photo synced) — even
          // when not in flight, typically a `failed` entry whose successful
          // photos created the record. Route it through the uploader ONCE
          // (guarded by `compensatingRef`): `uploadMultiPhotoRecord`
          // (deletedAtStart) fires the compensating DELETE by id, the mutation's
          // retry/backoff covers a transient failure, and `onSuccess` purges on
          // success. The once-per-session guard keeps a PERMANENT failure (e.g.
          // a 403 after losing edit permission) from re-firing on every query
          // invalidation — i.e. no tight retry loop. An entry with no recorded
          // server record (a single-photo entry, or a multi entry no photo of
          // which ever landed) was never created → purge it locally, no server call.
          if (entry.serverRecordId) {
            if (!compensatingRef.current.has(entry.clientUuid)) {
              compensatingRef.current.add(entry.clientUuid);
              inFlightRef.current.add(entry.clientUuid);
              uploader.mutate({ clientUuid: entry.clientUuid });
            }
          } else {
            attemptedRef.current.delete(entry.clientUuid);
            void deletePendingRecord(entry.clientUuid);
            purgedAny = true;
          }
        }
        // A deleted entry that IS in flight is left for `onSuccess` to
        // reconcile into a compensating DELETE. Either way, never a fresh create.
        continue;
      }
      if (entry.status !== "pending") continue;
      if (inFlight) continue;
      inFlightRef.current.add(entry.clientUuid);
      uploader.mutate({ clientUuid: entry.clientUuid });
    }
    // Refresh the drain/grid queries so a purged deleted entry disappears.
    if (purgedAny) {
      void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
    }
  }, [pendingRecords, uploader, queryClient]);
}
